import { Dispatch } from 'react-redux';

import { StoreState } from '../../../reducers';
import { Action } from '../../../actions';
import { savePage } from './pagesController';
import { setThumbnailsToUpdate } from './thumbnails';
import { saveConfigs } from './configs';
import { EditableSliderData } from './editableItems';
import { addToUndos } from './history';
import { Source } from '../reducer/history';
import { BackgroundMediaData, BackgroundSliderData, BackgroundMediaType } from '../reducer/configs';
import { ImportingSlideData } from '../utils/indesignEditor/editableSliders/getData';
import { getDependencyString, getMediaUrl } from '../utils/helpers';
import { getGeneralBgData, getSpreadBgData, BackgroundData } from '../selectors';
import { getAspDriveItem } from '../../DriveItemList/actions';
import {
	importFile,
	addFileInImport,
	FileError,
	ImportResponse,
} from '../../ImportManager/actions';
import { Api } from '../../../typings/api';

export enum SpreadBackgroundActionKeys {
	SET_SPREAD_BACKGROUND = '@@editor/spreadBackground/SET_SPREAD_BACKGROUND',
	SET_IS_SPREAD_BACKGROUND_ACTIVE = '@@editor/spreadBackground/SET_IS_SPREAD_BACKGROUND_ACTIVE',
	UPDATE_SPREAD_BACKGROUND_SERVICE_DATA = '@@editor/spreadBackground/UPDATE_SPREAD_BACKGROUND_SERVICE_DATA',
}

interface SetSpreadBackgroundAction extends Action {
	type: SpreadBackgroundActionKeys.SET_SPREAD_BACKGROUND;
	spread: number[];
	data: BackgroundMediaData | null;
	isGeneralBgData: boolean;
	shouldSetIsEdited: boolean;
}

interface SetIsSpreadBackgroundActiveAction extends Action {
	type: SpreadBackgroundActionKeys.SET_IS_SPREAD_BACKGROUND_ACTIVE;
	isActive: boolean;
}

interface UpdateSpreadBackgroundServiceDataAction extends Action {
	type: SpreadBackgroundActionKeys.UPDATE_SPREAD_BACKGROUND_SERVICE_DATA;
	page: number;
	data: EditableSliderData;
}

export type SpreadBackgroundAction =
	SetSpreadBackgroundAction |
	SetIsSpreadBackgroundActiveAction |
	UpdateSpreadBackgroundServiceDataAction;

export const setSpreadBackground = (
	spread: number[],
	data: BackgroundMediaData | null,
	isGeneralBgData: boolean,
	shouldSave: boolean = true,
	shouldSetIsEdited: boolean = true,
	shouldAddToUndos: boolean = true,
) => (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
	if (shouldAddToUndos) {
		const {
			editor: {
				pagesController: { spreads, currentSpreadIndex },
				configs: { pagesData },
			},
		} = getState();
		const page = spreads[currentSpreadIndex][0];

		dispatch(addToUndos({
			source: Source.SPREAD_BACKGROUND,
			data: pagesData[page - 1].bgStyle,
			page,
		}));
	}

	const action: SetSpreadBackgroundAction = {
		type: SpreadBackgroundActionKeys.SET_SPREAD_BACKGROUND,
		spread,
		data,
		isGeneralBgData,
		shouldSetIsEdited,
	};
	dispatch(action);

	if (shouldSave) {
		dispatch(saveConfigs());
	}
};

export const saveSpreadBackgroundPages = () => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const {
		editor: {
			pagesController: { spreads, currentSpreadIndex },
		},
	} = getState();
	const spread = spreads[currentSpreadIndex];
	dispatch(setThumbnailsToUpdate(spread));
	spread.forEach(page => dispatch(savePage(page)));
};

export const setIsSpreadBackgroundActive = (isActive: boolean) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const {
		editor: {
			spreadBackground: { isEdited },
		},
	} = getState();

	const action = {
		type: SpreadBackgroundActionKeys.SET_IS_SPREAD_BACKGROUND_ACTIVE,
		isActive,
	};
	dispatch(action);

	if (!isActive && isEdited) {
		dispatch(saveSpreadBackgroundPages());
	}
};

export const updateSpreadBackgroundServiceData = (
	page: number,
	data: EditableSliderData,
	shouldSave: boolean = true,
	shouldUpdateThumbnails?: boolean,
) => (dispatch: Dispatch<StoreState>) => {
	const action: UpdateSpreadBackgroundServiceDataAction = {
		type: SpreadBackgroundActionKeys.UPDATE_SPREAD_BACKGROUND_SERVICE_DATA,
		page,
		data,
	};
	dispatch(action);

	if (shouldUpdateThumbnails) {
		dispatch(setThumbnailsToUpdate([page]));
	}

	if (shouldSave) {
		dispatch(savePage(page));
	}
};

export const importSlidesToSpreadBackground = (
	page: number,
	newSlidesData: ImportingSlideData[],
	isFromDesktop?: boolean,
) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const {
		editor: {
			pagesController: { spreads, currentSpreadIndex },
			spreadBackground: { pagesData },
		},
	} = getState();
	const spread = spreads[currentSpreadIndex];
	const backgroundData = getSpreadBgData(getState(), spread) || getGeneralBgData(getState()) as BackgroundData;
	const { currentMedia, defaultMedia } = backgroundData;
	const { slides: prevSlides } = currentMedia && currentMedia.type === BackgroundMediaType.SLIDER && 'slides' in currentMedia
		? currentMedia
		: (defaultMedia as BackgroundSliderData);
	const prevImportingSlides = pagesData[page] && pagesData[page].importingSlides || [];
	const newImportingSlides = [...prevImportingSlides, ...newSlidesData];

	dispatch(updateSpreadBackgroundServiceData(page, {
		importingSlides: newImportingSlides,
	}, false));

	let importingSlides = newImportingSlides;

	while (importingSlides.length > 0) {
		const slideData = importingSlides[0];

		if (isFromDesktop) {
			const { id: slideId, file } = slideData;
			await dispatch(importFile({
				id: slideId,
				file: file as File,
				isOptimizedImageImport: true,
				isPublicationDependency: true,
				onImport: async ({ id }: Api.DriveItem) => {
					return await dispatch(addSlide(page, slideData, id));
				},
			}));
		} else {
			await dispatch(addSlide(page, slideData));
		}

		const {
			editor: { spreadBackground: { pagesData } },
			importManager: { byId },
		} = getState();
		const currentImportingSlides = pagesData && pagesData[page].importingSlides || [];
		const getSlidesWithoutImportError = (({ id }: ImportingSlideData): boolean => (
			byId[id] ? !byId[id].error : true
		));
		importingSlides = currentImportingSlides.filter(getSlidesWithoutImportError);
	}

	dispatch(saveConfigs());
	dispatch(savePage(page));

	if (prevSlides.length === 0) {
		dispatch(setThumbnailsToUpdate([page]));
	}
};

const addSlide = (
	page: number,
	slideData: ImportingSlideData,
	resourceGlobalId?: number,
) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void | ImportResponse> => {
	const { id: slideId, globalId } = slideData;
	const aspDriveItem = await dispatch(getAspDriveItem(resourceGlobalId || globalId as number));

	if (aspDriveItem) {
		const { type, label, relUrl, genericItemId, globalId, resolution, iconName } = aspDriveItem;
		const newSlideData = {
			...slideData,
			type,
			label,
			relUrl: getMediaUrl(relUrl),
			genericItemId,
			globalId,
			resolution,
			iconName,
		};
		const {
			editor: {
				pagesController: { spreads, currentSpreadIndex },
				spreadBackground: { pagesData },
			},
		} = getState();
		const spread = spreads[currentSpreadIndex];
		const backgroundData = getSpreadBgData(getState(), spread) || getGeneralBgData(getState()) as BackgroundData;
		const sliderData = backgroundData.currentMedia as BackgroundSliderData;
		const importingSlides = pagesData[page] && pagesData[page].importingSlides || [];
		const deps = pagesData[page] && pagesData[page].deps || [];
		const isGeneralBgData = !getSpreadBgData(getState(), spread) && !!getGeneralBgData(getState());

		dispatch(updateSpreadBackgroundServiceData(page, {
			importingSlides: importingSlides.filter(({ id }) => id !== slideId),
			deps: [...deps, getDependencyString(newSlideData.globalId)],
		}, false));
		dispatch(setSpreadBackground(spread, {
			...sliderData,
			slides: [...sliderData.slides, newSlideData],
		}, isGeneralBgData, false, false));
		return;
	} else {
		dispatch(addFileInImport(slideId, {
			error: FileError.IMPORT,
		}));

		return { isError: true };
	}
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/actions/spreadBackground.ts